/* eslint-disable max-lines */
import {
  ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy,
  OnInit, Output,
} from '@angular/core';
import { zip, Subscription, of } from 'rxjs';
import { Router } from '@angular/router';
import { take, map, tap, switchMap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';

import { isNullOrUndefined } from '@app/util/util';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import {
  InclusionType, UpsellResponse, UpsellInclusionType, UpsellOptions, UpsellInclusions, UpsellDataVM
} from '@shared/flight-itinerary/entities/upsell-flights';
import { bookingBaseRoute } from '@booking/constants/booking-routes.constants';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { AMPLITUDE_EVENTS } from '@app/modules/core/constants/amplitude.constants';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { BookingFacade } from '@booking/booking.facade';
import { PackagesResultFacade } from '@packages/packages-result/store/packages-result.facade';
import { environment as ENV } from '@environment';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { UpsellFacade } from '@upsell/upsell.facade';
import { FlightResultDesktopVm } from '@app/entities/flight-results';
import { EventProperties } from '@avo/interface/trigger-props';
import {
  flightAmplitudeProductPropertiesMapper
} from '@app/modules/core/mappers/flights-amplitude.mapper';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { TriggerAmplitudeEntity } from '@amplitude/interfaces/trigger-entity';
import { airPackagePropsFromUpsellResults } from '@amplitude/mappers/package-amplitude.mapper';
import { LuggageService } from '@shared/luggage/services/luggage.service';
import { AirValidationFlightsSector } from '@app/entities/air-validation.response';
import { showUserTag } from '@ux/components/member-rate-tag/mappers/tag.mapper';
import { TagType } from '@ux/components/member-rate-tag/entities/tag-type';
import { cabinTypeNameMapper } from '@flight-results/mappers/flights.mapper';
import { TranslatePipe } from '@app/translate/TranslatePipe';
import { mapflightClassUnique } from '@upsell/mappers/upsell.mapper';
import { TypographyColor } from '@design-system/components/atoms/typography/entities/typography.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import { ButtonIconSize } from '@design-system/components/molecules/button-icon/entities/button-icon.types';

@Component({
  selector: 'app-desktop-upsell',
  templateUrl: './desktop-upsell.component.html',
  styleUrls: ['./desktop-upsell.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DesktopUpsellComponent implements OnInit, OnDestroy {

  @Input() isPackages: boolean = false;
  @Input() itinerary: FlightResultDesktopVm;
  @Input() firstFlightByTripOption: AirValidationFlightsSector;
  @Input() isLoading: boolean;
  @Input() fligthsUpsell: UpsellResponse;
  @Input() defaultOption: number | null = null;
  @Input() isPrime: boolean;
  @Input() hasRappiCard: boolean;

  @Output() toggleShowForm: EventEmitter<void> = new EventEmitter();

  public upsellOption: UpsellOptions;

  public readonly radioLabelColor = TypographyColor.Secondary;

  public readonly buttonIconSize = ButtonIconSize.LG;

  public readonly icons = Icon;

  private optionIndex: number = 1;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private amplitude: PropertiesAmplitudeService,
    private baseSearchBarFacade: BaseSearchBarFacade,
    private bookingFacade: BookingFacade,
    private domManipulateService: DomManipulateService,
    private packagesResultFacade: PackagesResultFacade,
    private router: Router,
    private upsellFacade: UpsellFacade,
    private triggerAmplitudeService: TriggerAmplitudeService,
    private readonly luggageService: LuggageService,
    private readonly translatePipe: TranslatePipe
  ) { }

  ngOnInit(): void {
    this.domManipulateService.setBodyScroll(false);
    this.registerViewUpsellOptionsAmplitudeEvent();
  }

  ngOnDestroy(): void {
    this.domManipulateService.setBodyScroll(true);
    this.upsellFacade.resetUpsellAction();
    this.subscriptions.unsubscribe();
  }

  get tagType(): TagType {
    return showUserTag(this.hasRappiCard, this.isPrime)
  }

  get hasCabins(): boolean {
    return !isNullOrUndefined(this.fligthsUpsell.UpsellOptions[0]?.CabinTypeByFlights);
  }

  public isAtCharge = (inclusionType: UpsellInclusionType, option: UpsellOptions): boolean => {
    return this.findInclusionByCode(inclusionType, option)?.Type === InclusionType.AtCharge;
  }

  public isIncluded = (inclusionType: UpsellInclusionType, option: UpsellOptions): boolean => {
    return this.findInclusionByCode(inclusionType, option)?.Type === InclusionType.Included;
  }

  get isOptionSelected(): boolean {
    return !!this.upsellOption;
  }

  get upsellData(): UpsellDataVM[] {
    const options: UpsellOptions[] = this.fligthsUpsell?.UpsellOptions;
    const inclusionsTitlesRef = this.fligthsUpsell?.UpsellInclusionTypes;
    const data: UpsellDataVM[] = [];
    const flightData = this.firstFlightByTripOption ? this.firstFlightByTripOption :
      this.itinerary ? this.itinerary?.Sectors?.[0].Options?.[0].Flights?.[0] : null;
    if (flightData) {
      const { FareFamilies, FareBasis, AirlineCode, DepartureDate, DepartureAirport, ArrivalAirport } = flightData;
      if (inclusionsTitlesRef && inclusionsTitlesRef.length > 0) {
        inclusionsTitlesRef.forEach((inclusionRef: UpsellInclusions) => {
          const optionInclusions = [];
          options.forEach((option: UpsellOptions) => {
            const inclusionInfo = option?.Families[0]?.Inclusions?.find(item => item.Code === inclusionRef.Code);
            optionInclusions.push({
              option,
              family: option.Families[0].Name,
              inclusionInfo: inclusionInfo
            });
          });
          data.push({ optionType: inclusionRef, optionInclusions: optionInclusions });
        });
      }
      return data;
    } else return [];
  }

  public getflightClassUnique(cabins: number[]): number[] {
    return mapflightClassUnique(cabins);
  }

  public flightClassText(cabin: number): string {
    return !isNullOrUndefined(cabin) ? 
      this.translatePipe.transform(`FLIGHT_RESULTS_MODULE.FLIGHT_CLASSES.${this.labelFlightCabin(cabin.toString())}`): '';
  }

  public labelFlightCabin(cabin: string): string {
    return cabinTypeNameMapper(cabin);
  }

  public classState(inclusionType: UpsellInclusionType, option: UpsellOptions): IconFillColor {
    const data = this.findInclusionByCode(inclusionType, option);
    switch (data?.Type) {
      case InclusionType.Included:
        return IconFillColor.Success;
      case InclusionType.AtCharge:
        return IconFillColor.Warning;
      default:
        return IconFillColor.Error;
    }
  }

  public showTitle(inclusionType: UpsellInclusionType, option: UpsellOptions): string {
    const data = this.findInclusionByCode(inclusionType, option);
    return data?.Type === InclusionType.Included ? data.Title ? data.Title : data.SubTitle ? data.SubTitle : '' : '';
  }

  public iconState(inclusionType: UpsellInclusionType, option: UpsellOptions): Icon {
    const data = this.findInclusionByCode(inclusionType, option);
    switch (data?.Type) {
      case InclusionType.Included:
        return Icon.Check;
      case InclusionType.AtCharge:
        return Icon.Monetization;
      default:
        return Icon.Close;
    }
  }

  public updateSelectedOption(upsellOption: UpsellOptions, index: number): void {
    this.upsellOption = upsellOption;
    this.optionIndex = index + 1;
  }

  public selectUpsell(): void {
    if (!this.isPackages) this.selectFlightUpsell();
    else this.selectPackageUpsell();
  }

  public isOptionChecked(optionId: number): boolean {
    return this.upsellOption?.OptionId === optionId;
  }

  public calcUpsellItemValues(fare: number, taxes: number): number {
    return fare + taxes;
  }

  private registerViewUpsellOptionsAmplitudeEvent(): void {
    if (!isEmpty(this.fligthsUpsell) && !this.isLoading) {
      if (isNullOrUndefined(this.defaultOption)) {
        this.upsellOption = this.fligthsUpsell.UpsellOptions[0];
      } else {
        this.forceUpdateSelectedOption();
      }
    }
  }

  private selectFlightUpsell(): void {
    let upsellId = '';
    let fligthsOptionId = 0;
    zip(
      of(this.fligthsUpsell),
      this.upsellFacade.searchPayload$.pipe(take(1)),
      this.amplitude.searchProperties$(SearchBy.FLIGHTS),
      this.bookingFacade.airValidation$.pipe(map(x => flightAmplitudeProductPropertiesMapper(x?.TripOption))),
    ).pipe(
      tap(([fligthsUpsell]) => {
        upsellId = fligthsUpsell.UpsellId;
        fligthsOptionId = fligthsUpsell.UpsellOptionId;
      }),
      map(([fligthsUpsell, searchPayload, searchProps, productProps]) => ({
        fligthsUpsell, searchPayload, searchProps,
        eventAddToCartProps: this.getAddToCardProps(fligthsUpsell),
        productProps: { 
          ...productProps, ...(!isNullOrUndefined(this.upsellOption?.HasPrivateFare)) && 
          { TRAVEL_HAS_MEMBER_RATE: !!this.upsellOption?.HasPrivateFare }
        },
        navigateCommands: [
          bookingBaseRoute, searchPayload.searchId, fligthsOptionId, 'null',
          TravelBookingType.FLIGHTS, upsellId, this.upsellOption.OptionId
        ]
      })),
      switchMap(({ eventAddToCartProps, searchProps, productProps, navigateCommands }) => zip(
        this.triggerAmplitudeService.trackAmplitudeEventProps$(
          AMPLITUDE_EVENTS.ADD_TO_CART, eventAddToCartProps, searchProps, productProps
        ).pipe(take(1)),
        of(navigateCommands)
      )),
      tap(([triggerAddToCard, navigateCommands]) => {
        this.trackEventAndRedirect(navigateCommands, triggerAddToCard);
      })
    ).subscribe().unsubscribe();
  }

  private selectPackageUpsell(): void {
    let upsellId = '';
    let fligthsOptionId = 0;
    zip(
      of(this.fligthsUpsell), this.baseSearchBarFacade.searchData$, this.packagesResultFacade.flightsPayload$.pipe(take(1)),
      this.packagesResultFacade.hotelSelected$.pipe(take(1)), this.amplitude.searchProperties$(SearchBy.PACKAGES).pipe(take(1)),
    ).pipe(
      tap(([fligthsUpsell]) => {
        upsellId = fligthsUpsell.UpsellId;
        fligthsOptionId = fligthsUpsell.UpsellOptionId;
      }),
      map(([fligthsUpsell, searchData, flightsPayload, hotelSelected, searchProps]) => ({
        fligthsUpsell, searchData, searchProps,
        eventAddToCartProps: this.getAddToCardProps(fligthsUpsell),
        eventAddToCartUpsellProductProps: airPackagePropsFromUpsellResults(false, hotelSelected, this.itinerary?.Sectors,
          this.itinerary?.Provider, this.getUpsellPrice()),
        eventAddToCartProductProps: airPackagePropsFromUpsellResults(false, hotelSelected, this.itinerary?.Sectors,
          this.itinerary?.Provider, this.getBasePrice(fligthsUpsell)),
        navigateCommands: [
          bookingBaseRoute,
          flightsPayload.SearchId, fligthsOptionId,
          (this.itinerary && this.itinerary['HotelSearchId'] ? this.itinerary['HotelSearchId'] : flightsPayload.SearchId),
          (this.itinerary && this.itinerary['HotelOptionId'] ?
            this.itinerary['HotelOptionId'] : flightsPayload.SelectedHotelOptionId),
          flightsPayload.RateIdHotel, TravelBookingType.PACKAGE_AIR,
          ENV.countryInfo.currency_code, ENV.countryInfo.iso_code, upsellId, this.upsellOption.OptionId]
      })),
      switchMap(({
        eventAddToCartProps, searchProps, eventAddToCartUpsellProductProps,
        eventAddToCartProductProps, navigateCommands
      }) => zip(
        this.triggerAmplitudeService.trackAmplitudeEventProps$(
          AMPLITUDE_EVENTS.ADD_TO_CART, eventAddToCartProps, searchProps, eventAddToCartProductProps
        ).pipe(take(1)),
        of(navigateCommands)
      )),
      tap(([triggerAddToCard, navigateCommands]) => {
        this.trackEventAndRedirect(navigateCommands, triggerAddToCard);
      })
    ).subscribe().unsubscribe();
  }

  private findInclusionByCode(inclusionType: UpsellInclusionType, option: UpsellOptions): UpsellInclusions {
    return option?.Families[0]?.Inclusions.find(item => item.Code === inclusionType.Code);
  }

  private getAddToCardUpsellProps(fligthsUpsell: UpsellResponse): EventProperties {
    return {
      INDEX: this.optionIndex,
      BRANDED_FARE: this.upsellOption.Families[0]?.Name,
      BASE_PRICE: this.getBasePrice(fligthsUpsell),
      UPSELL_PRICE: this.getUpsellPrice()
    };
  }

  private getAddToCardProps(fligthsUpsell: UpsellResponse): EventProperties {
    return {
      INDEX: this.optionIndex,
      BASE_PRICE: this.getBasePrice(fligthsUpsell)
    };
  }

  private trackEventAndRedirect(navigateCommands: (string | number)[],
    triggerAddToCart: TriggerAmplitudeEntity): void {
    triggerAmplitudeEventApp(triggerAddToCart);
    this.toggleShowForm.emit();
    this.router.navigate(navigateCommands);
  }

  private getBasePrice(fligthsUpsell: UpsellResponse): number {
    return this.calcUpsellItemValues(fligthsUpsell.UpsellOptions[0].Amount.Fare, fligthsUpsell.UpsellOptions[0].Amount.Taxes);
  }

  private getUpsellPrice(): number {
    return this.calcUpsellItemValues(this.upsellOption.Amount.Fare, this.upsellOption.Amount.Taxes);
  }

  private forceUpdateSelectedOption(): void {
    const upsellOption = this.fligthsUpsell?.UpsellOptions?.find(option => option.OptionId === this.defaultOption);
    if (upsellOption) {
      const index = this.fligthsUpsell?.UpsellOptions?.findIndex(option => option.OptionId === this.defaultOption);
      this.updateSelectedOption(upsellOption, index);
    }
  }
}
